import Commons from '../../../Services/Common/Common';
import dayjs from 'dayjs';
import {
  buildPopBasicFilePath,
  decodePopBasicFileSegment,
} from './POP_BASIC_FileUtil';

/** 팝업 유형 — TB_POP_POPUP.POP_TYPE */
export const POP_BASIC_TYPE_OPTIONS = [
  { value: 'GENERAL', label: '일반 공지' },
  { value: 'SAFETY_LAW', label: '법령 안내(필독)' },
  { value: 'FORCE_CONFIRM', label: '강제 동의형' },
];

/** 노출 형태 — TB_POP_POPUP.DISPLAY_LAYER */
export const POP_BASIC_DISPLAY_LAYER_OPTIONS = [
  { value: 'MODAL', label: '중앙 모달' },
  { value: 'TOAST_TR', label: '우측 상단' },
  { value: 'TOAST_BR', label: '우측 하단' },
];

/** 링크 URL 입력이 필요한 노출 형태 */
export const POP_BASIC_LINK_URL_LAYERS = ['TOAST_TR', 'TOAST_BR'];

/** 노출 대상 — TB_POP_POPUP.TARGET_TYPE */
export const POP_BASIC_TARGET_TYPE_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'COMPANY', label: '특정 회사(선택)' },
];

export const POP_BASIC_DISPLAY_LAYER = 'MODAL';
export const POP_BASIC_TARGET_TYPE = 'ALL';

export const getPopBasicTypeLabel = (value) =>
  POP_BASIC_TYPE_OPTIONS.find((o) => o.value === value)?.label ??
  String(value ?? '');

export const getPopBasicDisplayLayerLabel = (value) =>
  POP_BASIC_DISPLAY_LAYER_OPTIONS.find((o) => o.value === value)?.label ??
  String(value ?? '');

export const getPopBasicTargetTypeLabel = (value) =>
  POP_BASIC_TARGET_TYPE_OPTIONS.find((o) => o.value === value)?.label ??
  String(value ?? '');

/** API/DB 파이프·콤마 구분 문자열 ↔ 배열 */
export const parsePopBasicList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (!value) return [];
  return String(value)
    .split(/[|,]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const joinPopBasicList = (arr) => {
  if (!Array.isArray(arr)) return '';
  return arr.filter(Boolean).map(String).join('|');
};

const formatDateTimeForApi = (value) => {
  if (!value) return '';
  const formatted = dayjs(value).format('YYYY-MM-DD HH:mm:ss');
  return formatted === 'Invalid Date' ? '' : formatted;
};

/** 신규 등록 시 노출 기간 기본값: 오늘 00:00 ~ 7일 후 23:59 */
export const createPopBasicDefaultDispPeriod = () => ({
  dispStartDt: dayjs().startOf('day').toDate(),
  dispEndDt: dayjs().add(7, 'day').endOf('day').toDate(),
});

const ynFromBool = (value) => (value === true || value === 'Y' ? 'Y' : 'N');

/**
 * POP_BASIC 스키마
 * - fields: 폼/그리드 공통 필드
 * - mapper: GetPopBasicR1 / SavePopBasicU1 / DeletePopBasicD1 payload
 */
export const POP_BASIC_Schema = {
  fields: {
    no: { initial: '', label: 'NO' },
    popId: { initial: '', label: '팝업ID' },
    coCd: { initial: '', label: '회사코드' },
    popType: { initial: 'GENERAL', label: '팝업 유형', required: 'Y' },
    title: { initial: '', label: '팝업 제목', required: 'Y' },
    content: { initial: '', label: '내용', required: 'Y' },
    targetType: {
      initial: POP_BASIC_TARGET_TYPE,
      label: '노출 대상',
      required: 'Y',
    },
    targetIndCds: { initial: [], label: '노출 업종' },
    targetCoCds: { initial: [], label: '노출 회사' },
    targetIndNms: { initial: '', label: '노출 업종명' },
    targetCoNms: { initial: '', label: '노출 회사명' },
    dispStartDt: { initial: null, label: '노출 시작 일시', required: 'Y' },
    dispEndDt: { initial: null, label: '노출 종료 일시', required: 'Y' },
    dispPriority: { initial: 1, label: '노출 우선순위' },
    isForceClose: {
      initial: 'N',
      label: '강제 확인(동의 필요)',
    },
    isHideToday: {
      initial: 'N',
      label: '오늘 하루 보지 않기 활성화',
    },
    displayLayer: {
      initial: POP_BASIC_DISPLAY_LAYER,
      label: '노출 형태',
      required: 'Y',
    },
    linkUrl: { initial: '', label: '링크 URL' },
    adminMemo: { initial: '', label: '관리 메모' },
    useYn: { initial: 'Y', label: '사용여부' },
    createId: { initial: '', label: '등록자' },
    createDate: { initial: '', label: '등록일시' },
    updateId: { initial: '', label: '수정자' },
    updateDate: { initial: '', label: '수정일시' },
    // 파일 (TB_POP_POPUP_FILE)
    popFileId: { initial: '', label: '파일ID' },
    fileName: { initial: '', label: '파일명' },
    saveName: { initial: '', label: '저장파일명' },
    filePath: { initial: '', label: '파일경로' },
    fileType: { initial: 'ATTACH', label: '파일유형' },
    fileSize: { initial: 0, label: '파일크기' },
    sortOrder: { initial: 0, label: '정렬순서' },
    previewFilePath: { initial: '', label: '미리보기경로' },
    preview: { initial: '', label: '미리보기URL' },
    deleted: { initial: false, label: '삭제여부' },
  },

  mapper: {
    search1: {
      /** ADMIN + CoCd 빈값 → 전체 회사 팝업 (GetPopBasicR1 SQL) */
      CoCd: (_, context) => {
        const coCd =
          context.userInfo?.authCd === 'ADMIN'
            ? context.search?.coCd ?? ''
            : context.userInfo?.coCd || '';
        return Commons.EncryptAES(coCd) || '';
      },
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    search2: {
      CoCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.coCd || '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    search3: {
      PopId: (_, context) =>
        Commons.EncryptAES(
          String(context.row?.popId || context.popId || '')
        ) || '',
      FileType: (_, context) =>
        Commons.EncryptAES(context.fileType || '') || '',
    },

    save1: {
      PopId: (row) => Commons.EncryptAES(row.popId || '') || '',
      CoCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.coCd || '') || '',
      PopType: (row) => Commons.EncryptAES(row.popType || 'GENERAL') || '',
      Title: (row) => Commons.EncryptAES(row.title || '') || '',
      Content: (row) => Commons.EncryptAES(row.content || '') || '',
      TargetType: (row) =>
        Commons.EncryptAES(row.targetType || POP_BASIC_TARGET_TYPE) || '',
      TargetIndCds: (row) =>
        Commons.EncryptAES(joinPopBasicList(row.targetIndCds)) || '',
      TargetCoCds: (row) =>
        Commons.EncryptAES(joinPopBasicList(row.targetCoCds)) || '',
      DispStartDt: (row) =>
        Commons.EncryptAES(formatDateTimeForApi(row.dispStartDt)) || '',
      DispEndDt: (row) =>
        Commons.EncryptAES(formatDateTimeForApi(row.dispEndDt)) || '',
      DispPriority: (row) =>
        Commons.EncryptAES(String(row.dispPriority ?? 1)) || '',
      IsForceClose: (row) =>
        Commons.EncryptAES(
          ynFromBool(row.isForceClose ?? row.IsForceClose ?? 'N')
        ) || '',
      IsHideToday: (row) =>
        Commons.EncryptAES(
          ynFromBool(row.isHideToday ?? row.IsHideToday ?? 'N')
        ) || '',
      DisplayLayer: (row) =>
        Commons.EncryptAES(
          row.displayLayer || POP_BASIC_DISPLAY_LAYER
        ) || '',
      LinkUrl: (row) => Commons.EncryptAES(row.linkUrl || '') || '',
      AdminMemo: (row) => Commons.EncryptAES(row.adminMemo || '') || '',
      UseYn: (row) =>
        Commons.EncryptAES(ynFromBool(row.useYn ?? row.UseYn ?? 'Y')) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    delete1: {
      PopId: (row) => Commons.EncryptAES(row.popId || '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    /** SavePopBasicU2 — NOT_BASIC_01_02 save2 패턴 (전송만 AES, DB는 서버 복호화 후 평문 저장) */
    save2: {
      PopFileId: (row) => Commons.EncryptAES(row.popFileId || '') || '',
      PopId: (row) => Commons.EncryptAES(String(row.popId || '')) || '',
      CoCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.coCd || '') || '',
      FileType: (row) => Commons.EncryptAES(row.fileType || 'ATTACH') || '',
      FileName: (row) =>
        Commons.EncryptAES(decodePopBasicFileSegment(row.fileName || '')) || '',
      SaveName: (row) =>
        Commons.EncryptAES(decodePopBasicFileSegment(row.saveName || '')) || '',
      FilePath: (row, context) => {
        const saveName = decodePopBasicFileSegment(row.saveName || '');
        if (!saveName) return '';
        const coCd = context.userInfo?.coCd ?? 'common';
        const popId = row.popId || '';
        const filePath =
          row.filePath || buildPopBasicFilePath(coCd, popId, saveName);
        return Commons.EncryptAES(filePath) || '';
      },
      FileSize: (row) =>
        Commons.EncryptAES(String(row.fileSize ?? 0)) || '',
      SortOrder: (row) =>
        Commons.EncryptAES(String(row.sortOrder ?? 0)) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    delete2: {
      PopFileId: (row) => Commons.EncryptAES(row.popFileId || '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    /** SavePopBasicU3 — TB_POP_POPUP_AGREE (강제확인 동의 / 오늘 하루 보지 않기) */
    save3: {
      PopId: (row) => Commons.EncryptAES(String(row.popId || '')) || '',
      CoCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.coCd || '') || '',
      CustCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.custCd || '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
      AgreeYn: (row) =>
        Commons.EncryptAES(ynFromBool(row.agreeYn ?? row.AgreeYn ?? 'Y')) || '',
      SignImage: (row) =>
        Commons.EncryptAES(row.signImage || row.SignImage || '') || '',
      HideUntilDt: (row) => {
        const v = row.hideUntilDt ?? row.HideUntilDt ?? '';
        if (!v) return '';
        const formatted = dayjs(v).format('YYYY-MM-DD');
        return formatted === 'Invalid Date'
          ? ''
          : Commons.EncryptAES(formatted) || '';
      },
    },
  },
};
